import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { emptyImageArrayFactory, emptyImageFactory, Image } from './_models/image';
import { NotificationService } from 'app/_services/notification.service';
import { Globals } from 'app/_models/globals';
import { fileSizeToMB, handleOnDropFile, inputFileToBase64 } from 'app/_utils/general-utils';
import { NgxUiLoaderService } from 'ngx-ui-loader';

declare const $: any;

@Component({
    selector: 'image-picker[imageQuantity]', templateUrl: './image-picker.component.html', styleUrls: ['./image-picker.component.scss'], encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ImagePickerComponent implements OnInit {
    private readonly ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'gif', 'png', 'bmp'];

    @Input() dynamicImages: boolean = false;
    @Input() imagesHaveOrder: boolean = false;

    @Input() imageQuantity: number;
    @Input() maxImagesQuantity: number = undefined;
    @Input() uploadMaxSizeAccepted: number = 5;
    @Input() maxSizeAcceptedMessage: string = 'componente.image-picker.images-size-info';

    @Output() onImageUpload: EventEmitter<any> = new EventEmitter<any>();
    @Output() onImageRemove: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('fileInput') fileInput: ElementRef;

    images: Image[];
    imageLoading: boolean = false;
    imagesHaveChanged: boolean;
    files: File[];

    get selectedImages(): Image[] {
        return this.images.filter(i => i.isSelected);
    }

    constructor(private notificationService: NotificationService, private ngxLoader: NgxUiLoaderService, private globals: Globals) {}

    ngOnInit() {
        this.images = emptyImageArrayFactory(this.imageQuantity);

        if (this.maxImagesQuantity == undefined)
            this.maxImagesQuantity = this.imageQuantity;
    }

    async handleInput($event, index: number) {
        this.addImage($event.target.files, index);
    }

    async addImage(files: File[], index: number = null) {
        if (index === null)
            index = this.images.length - 1;

        if (this.selectedImages.length + files.length > this.maxImagesQuantity) {
            await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-limit', { maxUploadImages: this.maxImagesQuantity.toString() });
            return false;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const isImageValid = await this.isImageValid(file);

            if (!isImageValid)
                continue;

            var imageB64 = await inputFileToBase64(file);
            this.addB64Image(imageB64, file.name, index);
            index++;
        }

        this.fileInput.nativeElement.value = '';
    }

    async addB64Image(imageB64: string, imageName: string, index: number = null) {
        if (index === null)
            index = this.images.length - 1;

        this.images[index] = {
            imageFile: imageB64.indexOf(',') > -1 ? imageB64.split(',')[1] : imageB64,
            imageName: imageName,
            isSelected: true,
            order: index
        }

        if (this.dynamicImages && this.images.length < this.maxImagesQuantity )
            this.addImageSlot();

        this.imageLoading = false;
        this.imagesHaveChanged = true;
    }

    addImageSlot() {
        this.images.push(emptyImageFactory(this.images.length + 1));
    }

    changeArrayOrder(oldIndex: number, newIndex: number) {
        var tmp = this.images[oldIndex];
        this.images[oldIndex] = this.images[newIndex];
        this.images[newIndex] = tmp;

        this.images[oldIndex].order = oldIndex;
        this.images[newIndex].order = newIndex;
    }

    changeImageOrderToLeft(index: number) {
        this.changeArrayOrder(index, index - 1);
    }

    changeImageOrderToRight(index: number) {
        this.changeArrayOrder(index, index + 1);
    }

    deletePicture(index: number) {
        if (!this.dynamicImages) {
            this.imagesHaveChanged = true;
            this.images[index] = emptyImageFactory(index);
            return;
        }

        if (this.selectedImages.length == this.maxImagesQuantity)
            this.addImageSlot();

        this.images.splice(index, 1);
    }

    async isImageValid(file: File): Promise<boolean> {
        if (this.selectedImages.find(i => i.imageName.toLowerCase() === file.name.toLowerCase())){
            await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-name', { fileName: file.name });
            return false;
        }

        let extension = file.type;
        let ext = extension.replace(/(.*)\//g, '').toLowerCase();

        if (!this.ALLOWED_EXTENSIONS.includes(ext))  {
            await this.notificationService.showWarningWithVariables('componente.image-picker.error.file-type', { fileName: file.name });
            return false;
        }

        let fileSizeMB = fileSizeToMB(file);

        if (fileSizeMB > this.uploadMaxSizeAccepted) {
            await this.notificationService.showWarningWithVariables('componente.image-picker.error.size-limit', { allowedMbs: this.uploadMaxSizeAccepted.toString() });
            return false;
        }

        return true;
    }

    onDrop($event): void {
        $event.preventDefault();

        if (!this.dynamicImages)
            return;

        var result = handleOnDropFile($event)

        this.addImage(result);
    }

    onDragOver($event: Event): void {
        $event.preventDefault();
    }

    openFileSelection($event) {
        $($event.target).next().click()
    }

    resetImages() {
        this.images = emptyImageArrayFactory(this.imageQuantity);

        if (this.maxImagesQuantity == undefined)
            this.maxImagesQuantity = this.imageQuantity;
    }

    startLoader() {
        this.ngxLoader.startLoader('loader-image-picker');
    }

    stopDrag($event: Event): void {
        $event.preventDefault();
        $event.stopPropagation();
    }

    stopLoader() {
        this.ngxLoader.stopLoader('loader-image-picker');
    }
}
